import {
  StepCaptureData,
  StepDetailsData,
} from "contexts/MultiStepCaptureContext";

import type { CompletedTaskSteps } from "lib/task-progress";

const COMPLETED_STEPS_TASK_ID = "__completed_steps__";
const DB_NAME = "BioidCaptureDB";
const DB_VERSION = 1;
const CAPTURE_STORE = "stepCaptures";
const DETAILS_STORE = "stepDetails";
const SESSION_LABEL_TASK_ID = "__session_labels__";

export interface StoredCaptureData {
  id: string; // taskId_step_stepNumber
  taskId: string;
  stepData: StepCaptureData;
  savedAt: number;
}

export interface StoredDetailsData {
  id: string; // taskId_step_stepNumber
  taskId: string;
  detailsData: StepDetailsData;
  savedAt: number;
}

interface StoredSessionLabelsData {
  id: string;
  taskId: typeof SESSION_LABEL_TASK_ID;
  sessionId: string;
  values: Record<string, string>;
  savedAt: number;
}

class CaptureIndexedDB {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private async initDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create captures store
        if (!db.objectStoreNames.contains(CAPTURE_STORE)) {
          const captureStore = db.createObjectStore(CAPTURE_STORE, {
            keyPath: "id",
          });
          captureStore.createIndex("taskId", "taskId", { unique: false });
          captureStore.createIndex("savedAt", "savedAt", { unique: false });
        }

        // Create details store
        if (!db.objectStoreNames.contains(DETAILS_STORE)) {
          const detailsStore = db.createObjectStore(DETAILS_STORE, {
            keyPath: "id",
          });
          detailsStore.createIndex("taskId", "taskId", { unique: false });
        }
      };
    });

    return this.dbPromise;
  }

  async saveStepCapture(
    taskId: string,
    stepData: StepCaptureData
  ): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction([CAPTURE_STORE], "readwrite");
    const store = transaction.objectStore(CAPTURE_STORE);

    const data: StoredCaptureData = {
      id: `${taskId}_step_${stepData.stepNumber}`,
      taskId,
      stepData,
      savedAt: Date.now(),
    };

    return new Promise<void>((resolve, reject) => {
      store.put(data);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async saveStepCaptures(
    taskId: string,
    steps: StepCaptureData[]
  ): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction([CAPTURE_STORE], "readwrite");
    const store = transaction.objectStore(CAPTURE_STORE);
    return new Promise<void>((resolve, reject) => {
      try {
        steps.forEach((stepData) =>
          store.put({
            id: `${taskId}_step_${stepData.stepNumber}`,
            taskId,
            stepData,
            savedAt: Date.now(),
          })
        );
      } catch (error) {
        transaction.abort();
        reject(error);
        return;
      }
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async saveStepDetails(
    taskId: string,
    detailsData: StepDetailsData
  ): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction([DETAILS_STORE], "readwrite");
    const store = transaction.objectStore(DETAILS_STORE);

    const data: StoredDetailsData = {
      id: `${taskId}_step_${detailsData.stepNumber}`,
      taskId,
      detailsData,
      savedAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      store.put(data);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async deleteStepData(taskId: string, stepNumber: number): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction(
      [CAPTURE_STORE, DETAILS_STORE],
      "readwrite"
    );
    const captureStore = transaction.objectStore(CAPTURE_STORE);
    const detailsStore = transaction.objectStore(DETAILS_STORE);

    const stepId = `${taskId}_step_${stepNumber}`;

    return new Promise((resolve, reject) => {
      captureStore.delete(stepId);
      detailsStore.delete(stepId);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getStepCaptures(taskId: string): Promise<StepCaptureData[]> {
    const db = await this.initDB();
    const transaction = db.transaction([CAPTURE_STORE], "readonly");
    const store = transaction.objectStore(CAPTURE_STORE);
    const index = store.index("taskId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(taskId);
      request.onsuccess = () => {
        const results = request.result as StoredCaptureData[];
        const stepData = results
          .map((r) => r.stepData)
          .sort((a, b) => a.stepNumber - b.stepNumber);
        resolve(stepData);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getStepDetails(taskId: string): Promise<StepDetailsData[]> {
    const db = await this.initDB();
    const transaction = db.transaction([DETAILS_STORE], "readonly");
    const store = transaction.objectStore(DETAILS_STORE);
    const index = store.index("taskId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(taskId);
      request.onsuccess = () => {
        const results = request.result as StoredDetailsData[];
        const detailsData = results
          .map((r) => r.detailsData)
          .sort((a, b) => a.stepNumber - b.stepNumber);
        resolve(detailsData);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async saveCompletedSteps(
    sessionId: string,
    taskId: string,
    steps: CompletedTaskSteps
  ): Promise<void> {
    const db = await this.initDB();
    const transaction = db.transaction([DETAILS_STORE], "readwrite");
    transaction.objectStore(DETAILS_STORE).put({
      id: `completed_steps_${sessionId}_${taskId}`,
      taskId: COMPLETED_STEPS_TASK_ID,
      steps,
      savedAt: Date.now(),
    });
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async getCompletedSteps(
    sessionId: string,
    taskId: string
  ): Promise<CompletedTaskSteps | null> {
    const db = await this.initDB();
    const transaction = db.transaction([DETAILS_STORE], "readonly");
    const request = transaction
      .objectStore(DETAILS_STORE)
      .get(`completed_steps_${sessionId}_${taskId}`);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const steps = request.result?.steps;
        if (!steps) return resolve(null);
        if (
          typeof steps.signature !== "string" ||
          !Array.isArray(steps.stepNumbers) ||
          !steps.stepNumbers.every(
            (step: unknown) =>
              typeof step === "number" && Number.isInteger(step) && step > 0
          )
        ) {
          return reject(new Error("Saved completed-step progress is invalid."));
        }
        resolve(steps);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async saveSessionLabels(
    sessionId: string,
    values: Record<string, string>
  ): Promise<void> {
    const nonEmptyValues = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value.trim() !== "")
    );
    if (Object.keys(nonEmptyValues).length === 0) return;

    const db = await this.initDB();
    const transaction = db.transaction([DETAILS_STORE], "readwrite");
    const store = transaction.objectStore(DETAILS_STORE);
    const id = `session_labels_${sessionId}`;
    const request = store.get(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const existing = request.result as StoredSessionLabelsData | undefined;
        store.put({
          id,
          taskId: SESSION_LABEL_TASK_ID,
          sessionId,
          values: { ...(existing?.values || {}), ...nonEmptyValues },
          savedAt: Date.now(),
        } satisfies StoredSessionLabelsData);
      };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  async getSessionLabels(sessionId: string): Promise<Record<string, string>> {
    const db = await this.initDB();
    const transaction = db.transaction([DETAILS_STORE], "readonly");
    const store = transaction.objectStore(DETAILS_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(`session_labels_${sessionId}`);
      request.onsuccess = () => {
        const result = request.result as StoredSessionLabelsData | undefined;
        resolve(result?.values || {});
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearTaskData(taskId: string): Promise<void> {
    const db = await this.initDB();

    // Clear captures
    await this.clearStore(db, CAPTURE_STORE, taskId);
    // Clear details
    await this.clearStore(db, DETAILS_STORE, taskId);
  }

  private async clearStore(
    db: IDBDatabase,
    storeName: string,
    taskId: string
  ): Promise<void> {
    const transaction = db.transaction([storeName], "readwrite");
    const store = transaction.objectStore(storeName);
    const index = store.index("taskId");

    return new Promise((resolve, reject) => {
      const request = index.getAllKeys(taskId);
      request.onsuccess = () => {
        const keys = request.result;
        keys.forEach((key) => store.delete(key));
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearAllData(): Promise<void> {
    const db = await this.initDB();

    const transaction = db.transaction(
      [CAPTURE_STORE, DETAILS_STORE],
      "readwrite"
    );
    const captureStore = transaction.objectStore(CAPTURE_STORE);
    const detailsStore = transaction.objectStore(DETAILS_STORE);

    return new Promise((resolve, reject) => {
      captureStore.clear();
      detailsStore.clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Clean up old captures (e.g., older than 7 days)
  async cleanupOldCaptures(daysOld: number = 7): Promise<void> {
    const db = await this.initDB();
    const cutoffTime = Date.now() - daysOld * 24 * 60 * 60 * 1000;

    const transaction = db.transaction([CAPTURE_STORE], "readwrite");
    const store = transaction.objectStore(CAPTURE_STORE);
    const index = store.index("savedAt");

    return new Promise<void>((resolve, reject) => {
      const range = IDBKeyRange.upperBound(cutoffTime);
      const request = index.getAllKeys(range);

      request.onsuccess = () => {
        const keys = request.result;
        keys.forEach((key) => store.delete(key));
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };
      request.onerror = () => reject(request.error);
    }).then(async () => {
      const detailsTransaction = db.transaction([DETAILS_STORE], "readwrite");
      const detailsStore = detailsTransaction.objectStore(DETAILS_STORE);

      await new Promise<void>((resolve, reject) => {
        const request = detailsStore.openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          const value = cursor.value as StoredSessionLabelsData;
          if (
            (value.taskId === SESSION_LABEL_TASK_ID ||
              (value.taskId as string) === COMPLETED_STEPS_TASK_ID) &&
            value.savedAt < cutoffTime
          ) {
            cursor.delete();
          }
          cursor.continue();
        };
        request.onerror = () => reject(request.error);
        detailsTransaction.oncomplete = () => resolve();
        detailsTransaction.onerror = () => reject(detailsTransaction.error);
        detailsTransaction.onabort = () => reject(detailsTransaction.error);
      });
    });
  }

  async hasStoredProgress(taskId: string): Promise<boolean> {
    const [captures, details] = await Promise.all([
      this.getStepCaptures(taskId),
      this.getStepDetails(taskId),
    ]);
    return captures.length > 0 || details.length > 0;
  }
}

// Export singleton instance
export const captureDB = new CaptureIndexedDB();
